<template>
  <div class="contact-page">
    <!-- 导航栏 -->
    <WebsiteNavbar />

    <!-- 顶部标题区域 -->
    <div class="hero-section">
      <div class="container">
        <h1 class="page-title">{{ safeT('contact.title', 'Contacts') }}</h1>
        <nav class="breadcrumb">
          <span class="breadcrumb-item">{{ safeT('contact.breadcrumb.industrialParts', 'Gyre Industrial Mechanical parts') }}</span>
          <span class="breadcrumb-separator">,</span>
          <span class="breadcrumb-item">{{ safeT('contact.breadcrumb.actuators', 'Pneumatic Actuators') }}</span>
          <span class="breadcrumb-separator">,</span>
          <span class="breadcrumb-item">{{ safeT('contact.breadcrumb.controllers', 'Pneumatic Controllers') }}</span>
          <span class="breadcrumb-separator">,</span>
          <span class="breadcrumb-item">{{ safeT('contact.breadcrumb.solutions', 'Customized Solutions') }}</span>
          <span class="breadcrumb-arrow">→</span>
          <span class="breadcrumb-current">{{ safeT('contact.breadcrumb.current', 'Contacts') }}</span>
        </nav>
      </div>
    </div>

    <!-- 联系信息主体区域 -->
    <div class="contact-content">
      <div class="container">
        <div class="logo-section">
          <img :src="logoImage" alt="Company Logo" class="company-logo" />
        </div>

        <h2 class="contact-title">{{ safeT('contact.heading', 'CONTACT US') }}</h2>

        <div class="contact-details">
          <div class="contact-item">
            <a :href="`mailto:${safeT('contact.email', 'hemu@gyansol.com')}`" class="contact-link email">
              {{ safeT('contact.email', 'hemu@gyansol.com') }}
            </a>
          </div>

          <div class="contact-item">
            <a :href="`https://wa.me/${safeT('contact.whatsapp', '+86 13357553736').replace(/[\s+-]/g, '')}`" target="_blank" rel="noopener noreferrer" class="contact-link whatsapp">
              <div class="contact-info">
                <svg class="whatsapp-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                <span class="phone-number">{{ safeT('contact.whatsapp', '+86 13357553736') }}</span>
              </div>
            </a>
          </div>

          <div class="contact-item">
            <div class="contact-info">
              <span class="tel-label">{{ safeT('contact.tel', 'TEL:') }}</span>
              <span class="phone-number">{{ safeT('contact.phone', '+8619290808990') }}</span>
            </div>
          </div>
        </div>

        <p class="service-promise">
          {{ safeT('contact.promise', 'We promise to provide an efficient response on every working day.') }}
        </p>
      </div>
    </div>

    <!-- 页脚 -->
    <WebsiteFooter />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import WebsiteNavbar from './components/WebsiteNavbar.vue'
import WebsiteFooter from './components/WebsiteFooter.vue'

const { t } = useI18n()

// Safe translation function to handle potential i18n compilation errors
const safeT = (key: string, defaultValue?: string): string => {
  try {
    const result = t(key)
    // If translation returns the key itself, it means translation was not found
    return result === key ? (defaultValue || key) : result
  } catch (error) {
    console.warn(`i18n translation error for key: ${key}`, error)
    return defaultValue || key
  }
}

const logoImage = new URL('@/assets/logo.png', import.meta.url).href
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@300;400;500;600;700&family=Hind:wght@300;400;500;600;700&display=swap');

.contact-page {
  font-family: 'Hind', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #333;
  overflow-x: hidden;
}

/* 顶部标题区域 */
.hero-section {
  background: linear-gradient(135deg, #8a8a8a 0%, #6a6a6a 100%);
  padding: 100px 0 60px;
  text-align: center;
  margin-top: 75px;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 50px;
}

.page-title {
  font-size: 56px;
  font-weight: 700;
  color: white;
  margin-bottom: 20px;
  font-family: 'Bitter', serif;
  letter-spacing: 1px;
}

.breadcrumb {
  font-size: 14px;
  color: white;
  opacity: 0.9;
  font-family: 'Hind', sans-serif;
  line-height: 1.8;
}

.breadcrumb-item {
  margin: 0 2px;
}

.breadcrumb-separator,
.breadcrumb-arrow {
  margin: 0 4px;
}

.breadcrumb-current {
  font-weight: 500;
  margin-left: 4px;
}

/* 联系信息主体区域 */
.contact-content {
  background: white;
  padding: 100px 0 120px;
  text-align: center;
}

.logo-section {
  margin-bottom: 50px;
}

.company-logo {
  width: 280px;
  height: auto;
}

.contact-title {
  font-size: 42px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 50px;
  font-family: 'Bitter', serif;
  letter-spacing: 2px;
}

.contact-details {
  max-width: 600px;
  margin: 0 auto 40px;
}

.contact-item {
  margin-bottom: 24px;
  font-size: 24px;
  color: #2c3e50;
  font-family: 'Hind', sans-serif;
}

.contact-link {
  color: #2c3e50;
  text-decoration: none;
  transition: color 0.3s ease;
  font-weight: 500;
}

.contact-link:hover {
  color: #d97f3e;
}

.contact-info {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
}

.whatsapp-icon {
  color: #25d366;
  flex-shrink: 0;
}

.tel-label {
  font-weight: 600;
  margin-right: 8px;
}

.phone-number {
  font-weight: 500;
}

.service-promise {
  font-size: 16px;
  color: #5a6c7d;
  font-family: 'Hind', sans-serif;
  font-weight: 400;
  margin-top: 50px;
  font-style: italic;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  .hero-section {
    padding: 80px 0 40px;
    margin-top: 65px;
  }

  .page-title {
    font-size: 36px;
  }

  .breadcrumb {
    font-size: 12px;
    line-height: 1.6;
  }

  .contact-content {
    padding: 60px 0 80px;
  }

  .company-logo {
    width: 200px;
  }

  .contact-title {
    font-size: 32px;
    margin-bottom: 40px;
  }

  .contact-item {
    font-size: 18px;
    margin-bottom: 20px;
  }

  .service-promise {
    font-size: 14px;
    margin-top: 40px;
  }
}
</style>